/**
 *  Widgets - Double Chance
 *  @module Widgets
 *  @author Livescore <info@score-stats.com>
 *  @copyright 2021 livescore
 */

import { MatchService } from '@services/match.service';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '@widgets/pages/base.component';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { DemoService } from '@widgets/services/demo.service';
import { WidgetMixin } from '@widgets/services/mixins/widget.mixin';

import { Form, LastForm } from '@widgets/interfaces/widget.interface';
import CONFIG from '@widgets/config/widget.config';
import SPORT_CONFIG from '@config/sport.config';

import { ClientService } from '@/services/client.service';
import { MatchData, Team } from '@/interfaces/match-data.interface';
import { LangService } from '@/services/lang.service';
import { MatchType } from '@/interfaces/league-table.interface';

@Component({
    selector: 'widgets-double-chance',
    templateUrl: './double-chance.component.html',
    styleUrls: ['../styles.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class DoubleChanceComponent extends WidgetMixin(BaseComponent) implements OnInit, OnDestroy {
    public matches: MatchData[] = [];

    private matchId: number;

    private subscriber: Subscription;

    public data: Form | null = null;

    public sportName: null | string = null;

    public constructor(
        private router: ActivatedRoute,
        private match: MatchService,
        private lang: LangService,
        private demo: DemoService,
        public client: ClientService,
    ) {
        super(demo);
    }

    /**
     * Load data from server
     */
    public ngOnInit(): void {
        super.ngOnInit();

        this.setTitle(this.router);

        this.router.data.subscribe((sub) => {
            this.num = sub.num;
            if (sub.br && sub.br.length > 0) {
                [this.matchId] = sub.br;
            } else {
                this.finished();
            }

            if (this.matchId) {
                this.load();
            } else {
                this.error();
            }
        });
    }

    public ngOnDestroy(): void {
        if (this.subscriber instanceof Subscription) {
            this.subscriber.unsubscribe();
        }

        this.matches.splice(0);
    }

    public result(type: string, participant: 'home' | 'away'):number {
        let winOrDraw = 0;
        let lose = 0;

        const reversed = this.data![participant].form.length > this.num
            ? this.data![participant].form.slice(1) : this.data![participant].form;
        reversed.forEach((f) => {
            if (!this.sportName) {
                this.sportName = f.sport_name ?? null;
            }

            if (f.code_state !== 'not_started' && f[type]) {
                const [home, away] = f[type].split(':');
                const isHome = this.data![participant].name === f.home_team;

                if ((isHome && +home > +away) || (!isHome && +home < +away) || +home === +away) {
                    winOrDraw += 1;
                } else {
                    lose += 1;
                }
            }
        });

        return Math.round((winOrDraw / (lose + winOrDraw)) * 100);
    }

    get hasPeriod3(): boolean {
        return SPORT_CONFIG.isHockey(this.sportName ?? '') ||
        SPORT_CONFIG.isFloorball(this.sportName ?? '');
    }

    get home1(): number {
        return this.result('score_period_1', 'home');
    }

    get home2(): number {
        return this.result('score_period_2', 'home');
    }

    get home3(): number {
        return this.result('score_period_3', 'home');
    }

    get away1(): number {
        return this.result('score_period_1', 'away');
    }

    get away2(): number {
        return this.result('score_period_2', 'away');
    }

    get away3(): number {
        return this.result('score_period_3', 'away');
    }

    get home(): number {
        return this.result('score_normal_time', 'home');
    }

    get away(): number {
        return this.result('score_normal_time', 'away');
    }

    get homeWidth(): number {
        return Math.round((this.home / (this.home + this.away)) * 100);
    }

    get awayWidth(): number {
        return 100 - this.homeWidth;
    }

    get homeWidth1(): number {
        return Math.round((this.home1 / (this.home1 + this.away1)) * 100);
    }

    get awayWidth1(): number {
        return 100 - this.homeWidth1;
    }

    get homeWidth2(): number {
        return Math.round((this.home2 / (this.home2 + this.away2)) * 100);
    }

    get awayWidth2(): number {
        return 100 - this.homeWidth2;
    }

    get homeWidth3(): number {
        return Math.round((this.home3 / (this.home3 + this.away3)) * 100);
    }

    get awayWidth3(): number {
        return 100 - this.homeWidth3;
    }

    get rankingHome(): number | string {
        return this.data!.home.tennis_rankings_single || '-';
    }

    get rankingAway(): number | string {
        return this.data!.away.tennis_rankings_single || '-';
    }

    private load(): void {
        const iso: string | null = this.lang.getLangSnapshot();
        let homeId: number | null = null;
        let awayId: number | null = null;

        this.subscriber = this.match
            .getMatchNew(this.matchId, iso as string, true)
            .pipe(
                filter((match: MatchData) => CONFIG.dblChance.availSports.includes(match.sport_code_name)),
                mergeMap(
                    (match: MatchData): Observable<Form> => {
                        const [p1, p2] = match.participants.map((p: Team): number => p.id);
                        homeId = p1;
                        awayId = p2;
                        return this.demo.form(p1, p2, iso, { limit: this.num });
                    },
                ),
                map((data: Form) => {
                    data.home = { ...data.home, id: homeId };
                    data.away = { ...data.away, id: awayId };

                    data.home.form = data.home.form.filter((f: LastForm) => f.type !== MatchType.UPCOMING);
                    data.away.form = data.away.form.filter((f: LastForm) => f.type !== MatchType.UPCOMING);
                    return data;
                }),
                switchMap(this.checkEmpty()),
                catchError(
                    (e): Observable<any> => {
                        this.error(e);
                        return throwError(e);
                    },
                ),
            )

            .subscribe(
                (data) => {
                    this.data = data;
                    this.finished();
                },
                () => {},
                () => {
                    this.loaded = true;
                },
            );
    }
}
